import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import apiKey from "../../../config/apiKey";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const MyAccount = () => {
  const { t } = useTranslation();
  const admin = JSON.parse(localStorage.getItem("edo-admin"));

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // use separate visibility toggles (better UX)
  const [show, setShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });

  const toggle = (key) => setShow((s) => ({ ...s, [key]: !s[key] }));

  const [formData, setFormData] = useState({
    password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  // ── Two-factor authentication (Google Authenticator) ──
  const [twoFa, setTwoFa] = useState({ loading: true, enabled: false });
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_url } while mid-setup, not yet confirmed
  const [confirmCode, setConfirmCode] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  useEffect(() => {
    axios
      .get(`${apiKey}/api/edolutions/admin/2fa/status`, { headers: { Authorization: `Bearer ${admin?.token}` } })
      .then((r) => setTwoFa({ loading: false, enabled: !!r.data?.data?.enabled }))
      .catch(() => setTwoFa({ loading: false, enabled: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTwoFaSetup = async () => {
    setSettingUp(true);
    try {
      const r = await axios.post(
        `${apiKey}/api/edolutions/admin/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      setSetupData(r.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Couldn't start 2FA setup.");
    } finally {
      setSettingUp(false);
    }
  };

  const confirmTwoFa = async (e) => {
    e.preventDefault();
    if (!confirmCode.trim()) return;
    setConfirmBusy(true);
    try {
      await axios.post(
        `${apiKey}/api/edolutions/admin/2fa/confirm`,
        { code: confirmCode.trim() },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      toast.success("Two-factor authentication is now on.");
      setTwoFa({ loading: false, enabled: true });
      setSetupData(null);
      setConfirmCode("");
    } catch (err) {
      toast.error(err.response?.data?.message || "That code is incorrect or expired.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const disableTwoFa = async (e) => {
    e.preventDefault();
    if (!disablePwd.trim()) return;
    setDisabling(true);
    try {
      await axios.post(
        `${apiKey}/api/edolutions/admin/2fa/disable`,
        { password: disablePwd },
        { headers: { Authorization: `Bearer ${admin?.token}` } }
      );
      toast.success("Two-factor authentication is now off.");
      setTwoFa({ loading: false, enabled: false });
      setDisablePwd("");
      setConfirmingDisable(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Incorrect password.");
    } finally {
      setDisabling(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: "" })); // clear field error on change
  };

  // simple client-side validation to avoid round-trips
  const validate = () => {
    const e = {};
    if (!formData.password.trim()) e.password = t("adminDash.myAccount.currentPasswordRequired");
    if (!formData.new_password.trim())
      e.new_password = t("adminDash.myAccount.newPasswordRequired");
    if (
      formData.new_password &&
      (formData.new_password.length < 8 || formData.new_password.length > 50)
    ) {
      e.new_password =
        t("adminDash.myAccount.newPasswordLength");
    }
    if (
      formData.new_password &&
      formData.new_password_confirmation &&
      formData.new_password !== formData.new_password_confirmation
    ) {
      e.new_password = t("adminDash.myAccount.confirmationDoesNotMatch");
      e.new_password_confirmation = t("adminDash.myAccount.doesNotMatchNewPassword");
    }
    return e;
  };

  const normalizeServerErrors = (err) => {
    // Backend might return: { errors: { password: ["..."], new_password:["..."] } }
    if (err?.response?.data?.errors) {
      const server = err.response.data.errors;
      const mapped = {};
      Object.keys(server).forEach((k) => {
        const v = server[k];
        mapped[k] = Array.isArray(v) ? v.join(" ") : String(v || "");
      });
      return mapped;
    }
    return { general: t("adminDash.adminAddSubject.unexpectedError") };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const eMap = validate();
    if (Object.keys(eMap).length) {
      setErrors(eMap);
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      await axios.post(`${apiKey}/api/edolutions/admin/newpassword`, formData, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      });

      toast.success(t("adminDash.myAccount.passwordUpdated"), {
        position: "bottom-right",
        autoClose: 5000,
        theme: "colored",
        style: { backgroundColor: "#2A3A54", color: "#fff" },
      });

      // Reset the form
      setFormData({
        password: "",
        new_password: "",
        new_password_confirmation: "",
      });
    } catch (err) {
      setErrors(normalizeServerErrors(err));
    } finally {
      setIsLoading(false);
    }
  };

  const ErrorText = ({ children }) =>
    children ? (
      <p className="text-red-600 text-xs mt-1" role="alert" aria-live="polite">
        *{children}
      </p>
    ) : null;

  const Field = ({
    label,
    name,
    type = "password",
    value,
    onChange,
    shown,
    onToggle,
    placeholder,
  }) => {
    const { t: tField } = useTranslation();
    return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <div className="bg-[#F9FBFF] rounded-lg px-2 flex items-center ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-[#22B7AA]">
        <input
          className="outline-none py-2 w-full bg-[#F9FBFF] text-sm"
          placeholder={label || placeholder}
          type={shown ? "text" : type}
          name={name}
          value={value}
          onChange={onChange}
          autoComplete="new-password"
        />
        <button
          type="button"
          className="ml-2 p-1 text-gray-500 hover:text-gray-700"
          onClick={onToggle}
          aria-label={shown ? tField("adminDash.myAccount.hidePassword") : tField("adminDash.myAccount.showPassword")}
        >
          {shown ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
    </label>
    );
  };

  return (
    <>
      <div className="flex items-center xl:p-5 sm:p-0 justify-center">
        <div className="w-full border rounded-3xl shadow-xl p-5 bg-white">
          <div>
            <p className="text-2xl font-bold">{t("adminDash.settings")}</p>

            <div className="flex border-b-2 justify-between items-center mt-6">
              <div className="flex flex-wrap md:space-x-12 space-x-5 text-base font-medium">
                <NavLink
                  to=".././my-account"
                  className={({ isActive }) =>
                    `pb-3 ${
                      isActive
                        ? "text-[#EE5F2B] border-b-2 border-[#EE5F2B]"
                        : "text-gray-600 hover:text-gray-900"
                    }`
                  }
                >
                  {t("adminDash.setting.myAccount")}
                </NavLink>
                {/* Future tabs can live here */}
              </div>
            </div>

            {/* Content */}
            <div className="lg:p-6">
              <div className="border-b-2 pb-4 mt-4">
                <p className="font-bold text-lg">{t("adminDash.myAccount.passwordChange")}</p>
              </div>

              <form className="mt-6 md:w-7/12" onSubmit={handleSubmit} noValidate>
                {/* Current password */}
                <Field
                  label={t("adminDash.myAccount.enterCurrentPassword")}
                  name="password"
                  value={formData.password}
                  onChange={onChange}
                  shown={show.current}
                  onToggle={() => toggle("current")}
                />
                <ErrorText>{errors.password}</ErrorText>

                {/* New password */}
                <div className="mt-4">
                  <Field
                    label={t("adminDash.myAccount.enterNewPassword")}
                    name="new_password"
                    value={formData.new_password}
                    onChange={onChange}
                    shown={show.next}
                    onToggle={() => toggle("next")}
                  />
                  <ErrorText>{errors.new_password}</ErrorText>
                </div>

                {/* Confirm */}
                <div className="mt-4">
                  <Field
                    label={t("adminDash.myAccount.reEnterNewPassword")}
                    name="new_password_confirmation"
                    value={formData.new_password_confirmation}
                    onChange={onChange}
                    shown={show.confirm}
                    onToggle={() => toggle("confirm")}
                  />
                  <ErrorText>{errors.new_password_confirmation}</ErrorText>
                </div>

                {/* General/server error */}
                <ErrorText>{errors.general}</ErrorText>

                <div className="mt-6">
                  <button
                    type="submit"
                    className={`text-sm font-medium inline-flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      isLoading
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : " text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B]"
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                    )}
                    {t("adminDash.myAccount.savePassword")}
                  </button>
                </div>
              </form>

              {/* Optional guidance */}
              <div className="mt-6 text-xs text-gray-500">
                {t("adminDash.myAccount.passwordTip")}
              </div>

              {/* Two-factor authentication */}
              <div className="border-t-2 pb-4 mt-8 pt-6">
                <p className="font-bold text-lg">Two-Factor Authentication</p>
                <p className="text-xs text-gray-500 mt-1">
                  Add an extra layer of security — after your password, you'll also need a 6-digit code from an app
                  like Google Authenticator to log in.
                </p>
              </div>

              <div className="mt-4 md:w-7/12">
                {twoFa.loading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : twoFa.enabled ? (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700 bg-green-50 rounded-lg px-3 py-1.5">
                        ✓ Two-factor authentication is on
                      </span>
                    </div>
                    {!confirmingDisable ? (
                      <button
                        type="button"
                        onClick={() => setConfirmingDisable(true)}
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Turn off 2FA
                      </button>
                    ) : (
                      <form onSubmit={disableTwoFa} className="space-y-3">
                        <label className="block">
                          <span className="text-xs text-gray-600">Confirm your password to turn off 2FA</span>
                          <input
                            type="password"
                            className="mt-1 w-full bg-[#F9FBFF] rounded-lg px-3 py-2 text-sm outline-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#22B7AA]"
                            value={disablePwd}
                            onChange={(e) => setDisablePwd(e.target.value)}
                            autoComplete="current-password"
                          />
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="submit"
                            disabled={disabling || !disablePwd.trim()}
                            className="text-sm font-medium px-4 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50"
                          >
                            {disabling ? "Turning off…" : "Turn off 2FA"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setConfirmingDisable(false); setDisablePwd(""); }}
                            className="text-sm font-medium px-4 py-2 rounded-lg text-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                ) : setupData ? (
                  <div className="bg-[#F9FBFF] rounded-xl p-5 ring-1 ring-gray-200">
                    <p className="text-sm font-semibold mb-3">1. Scan this with Google Authenticator (or any TOTP app)</p>
                    <div className="bg-white inline-block p-3 rounded-lg ring-1 ring-gray-200 mb-3">
                      <QRCodeSVG value={setupData.otpauth_url} size={168} />
                    </div>
                    <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this key manually:</p>
                    <code className="block text-xs bg-white rounded px-2 py-1.5 ring-1 ring-gray-200 mb-4 break-all">
                      {setupData.secret}
                    </code>

                    <form onSubmit={confirmTwoFa} className="space-y-3">
                      <label className="block">
                        <span className="text-sm font-semibold">2. Enter the 6-digit code from the app</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          className="mt-1 w-40 bg-white rounded-lg px-3 py-2 text-sm outline-none ring-1 ring-gray-200 focus:ring-2 focus:ring-[#22B7AA] tracking-widest"
                          value={confirmCode}
                          onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ""))}
                          placeholder="000000"
                          autoComplete="one-time-code"
                        />
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={confirmBusy || confirmCode.length !== 6}
                          className="text-sm font-medium px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] disabled:opacity-50"
                        >
                          {confirmBusy ? "Verifying…" : "Confirm & turn on"}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setSetupData(null); setConfirmCode(""); }}
                          className="text-sm font-medium px-4 py-2 rounded-lg text-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={startTwoFaSetup}
                    disabled={settingUp}
                    className="text-sm font-medium inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] hover:from-[#2A3A54] hover:to-[#EE5F2B] disabled:opacity-50"
                  >
                    {settingUp ? "Starting…" : "Set up two-factor authentication"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyAccount;
